/**
 * 로그인 관문 — 로그인 전에는 로그인 / 가입 신청 화면, 로그인 후에는 앱
 * 사용자 정보·로그아웃은 useAuth()로
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { TermsDialog } from './Terms';
import Landing from './Landing';
import { AuthUser, fetchMe, getToken, login, setToken, errMsg, signup, SignupForm, changePassword, findId, resetPassword, fetchFormToken } from '../utils/api';

const AuthCtx = createContext<{ user: AuthUser; logout: () => void; toSignup?: () => void; toLogin?: () => void; update?: (u: AuthUser) => void } | null>(null);
/** 로그인 없이 체험하는 사람 (저장·작업 관리 없음) */
export const GUEST: AuthUser = { id: 'guest', name: '체험', role: 'GUEST', compId: 0, compName: '무료 체험' };
export const useAuth = () => useContext(AuthCtx)!;

// 토스 스타일: 채운 입력칸 · 큰 둥근 카드 · 파랑 큰 버튼
const input: React.CSSProperties = { width: '100%', height: 50, padding: '0 14px', fontSize: 16, border: '1.5px solid transparent', borderRadius: 12,
  background: '#F2F4F6', color: '#191F28', boxSizing: 'border-box' };
const card: React.CSSProperties = { width: '100%', background: '#fff', borderRadius: 24, padding: 32, boxShadow: '0 12px 40px rgba(0,0,0,0.06)' };
const primary = (on: boolean): React.CSSProperties => ({
  width: '100%', height: 54, fontSize: 16, fontWeight: 700, color: '#fff', border: 'none', borderRadius: 14,
  background: on ? '#3182F6' : '#B0D0FB', cursor: on ? 'pointer' : 'default',
});
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#3182F6', cursor: 'pointer', fontSize: 14, padding: 0, fontWeight: 600 };
const labelSt: React.CSSProperties = { display: 'block', fontSize: 13, color: '#8B95A1', fontWeight: 500, marginBottom: 6 };
/** 위 로고 줄 */
const Brand: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22 }}>
    <img src="/icon.svg" alt="" width={28} height={28} /><b style={{ fontSize: 18, color: '#191F28', letterSpacing: -0.3 }}>Panel Draw</b>
  </div>
);

/** 사업자등록번호 입력 모양: 123-45-67890 */
const fmtBiz = (s: string) => {
  const d = s.replace(/\D/g, '').slice(0, 10);
  return d.length > 5 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d;
};
/** 사업자등록번호 검증번호 (서버와 같은 규칙 — 미리 알려주기용) */
const bizOk = (s: string) => {
  const d = s.replace(/\D/g, '');
  if (d.length !== 10) return false;
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = w.reduce((a, x, i) => a + Number(d[i]) * x, 0) + Math.floor((Number(d[8]) * 5) / 10);
  return (10 - (sum % 10)) % 10 === Number(d[9]);
};

const EMPTY: SignupForm = { compNm: '', bizNo: '', ceoNm: '', tel: '', addr: '', mgrNm: '', mgrTel: '', mgrEmail: '', memo: '', userId: '', userNm: '', pw: '', agree: false };

const Signup: React.FC<{ onDone: () => void; onBack: () => void }> = ({ onDone, onBack }) => {
  const [f, setF] = useState<SignupForm>(EMPTY);
  const [pw2, setPw2] = useState('');
  const [hp, setHp] = useState(''); // 숨은 칸 (봇만 채움)
  const [formToken, setFormToken] = useState('');
  useEffect(() => { fetchFormToken().then(r => setFormToken(r.data.token)).catch(() => {}); }, []);
  const [showTerms, setShowTerms] = useState(false);
  const [bizFile, setBizFile] = useState<{ name: string; data: string; size: number } | null>(null);
  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) { setBizFile(null); return; }
    if (file.size > 5 * 1024 * 1024) { setErr('파일은 5MB 이하만 올릴 수 있습니다'); e.target.value = ''; return; }
    const rd = new FileReader();
    rd.onload = () => setBizFile({ name: file.name, data: String(rd.result), size: file.size });
    rd.readAsDataURL(file);
  };
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof SignupForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF(p => ({ ...p, [k]: k === 'bizNo' ? fmtBiz(e.target.value) : e.target.value }));
  const check =
    !f.compNm.trim() ? '상호를 입력하세요'
    : !bizOk(f.bizNo) ? '사업자등록번호를 확인하세요'
    : !f.ceoNm.trim() ? '대표자를 입력하세요'
    : !f.mgrNm.trim() ? '담당자를 입력하세요'
    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.mgrEmail) ? '담당자 이메일을 확인하세요'
    : !bizFile ? '사업자등록증을 첨부하세요'
    : !f.tel.trim() && !f.mgrTel.trim() ? '연락처를 입력하세요'
    : !/^[a-zA-Z0-9_.-]{4,20}$/.test(f.userId) ? '아이디는 영문·숫자 4~20자'
    : f.pw.length < 8 ? '비밀번호는 8자 이상'
    : f.pw !== pw2 ? '비밀번호 확인이 다릅니다'
    : !f.agree ? '약관에 동의해 주세요'
    : null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (check) { setErr(check); return; }
    setBusy(true); setErr(null);
    try { await signup(f, { website: hp, formToken, bizFile: { name: bizFile!.name, data: bizFile!.data } }); onDone(); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  // 넓은 화면 3칸·좁은 화면 1칸 — 세로 길이를 줄임
  const wide = window.innerWidth >= 720;
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: wide ? 'repeat(3, minmax(0, 1fr))' : '1fr', gap: '12px 12px' };
  const span = (n: number): React.CSSProperties => (wide ? { gridColumn: `span ${n}` } : {});
  const cin: React.CSSProperties = { ...input, height: 44, fontSize: 15, marginTop: 6, borderRadius: 10 };
  const lab = (label: string, req?: boolean) => <span style={{ fontSize: 13, color: '#8B95A1', fontWeight: 500 }}>{label}{req && <b style={{ color: '#F04452' }}> *</b>}</span>;
  const row = (label: string, k: keyof SignupForm, opt: { req?: boolean; type?: string; ph?: string; auto?: string; span?: number } = {}) => (
    <label style={{ display: 'block', ...span(opt.span ?? 1) }}>
      {lab(label, opt.req)}
      <input type={opt.type ?? 'text'} value={f[k] as string} onChange={set(k)} placeholder={opt.ph} autoComplete={opt.auto} style={cin} />
    </label>
  );
  const sec: React.CSSProperties = { fontSize: 16, fontWeight: 700, color: '#191F28', margin: '24px 0 10px' };
  const biz = f.bizNo.replace(/\D/g, '');
  return (
    <form onSubmit={submit} style={{ ...card, maxWidth: 880, padding: wide ? '32px 36px' : 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#191F28', letterSpacing: -0.5 }}>회사 가입 신청</h1>
        <span style={{ fontSize: 14, color: '#8B95A1' }}>신청 후 관리자 승인이 끝나면 사용할 수 있습니다.</span>
      </div>

      <input value={hp} onChange={e => setHp(e.target.value)} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
        style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} />
      <div style={sec}>회사 정보</div>
      <div style={grid}>
        {row('상호', 'compNm', { req: true })}
        <label style={{ display: 'block' }}>
          {lab('사업자등록번호', true)}
          <input value={f.bizNo} onChange={set('bizNo')} placeholder="123-45-67890" inputMode="numeric" style={cin} />
          {biz.length === 10 && <span style={{ fontSize: 11, color: bizOk(biz) ? '#03B26C' : '#F04452' }}>{bizOk(biz) ? '✓ 올바른 번호' : '⚠ 번호를 다시 확인하세요'}</span>}
        </label>
        {row('대표자', 'ceoNm', { req: true })}
        {row('회사 전화', 'tel', { ph: '02-123-4567', auto: 'tel' })}
        {row('주소', 'addr', { span: 2 })}
        <label style={{ display: 'block', ...span(3) }}>
          {lab('사업자등록증 (사진 또는 PDF, 5MB 이하)', true)}
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={pickFile} style={{ ...cin, padding: '10px 12px', height: 'auto' }} />
          {bizFile && <span style={{ fontSize: 11, color: '#03B26C' }}>✓ {bizFile.name} ({Math.round(bizFile.size / 1024)}KB)</span>}
        </label>
      </div>

      <div style={sec}>담당자</div>
      <div style={grid}>
        {row('이름', 'mgrNm', { req: true, auto: 'name' })}
        {row('휴대폰', 'mgrTel', { ph: '010-1234-5678', auto: 'tel' })}
        {row('이메일', 'mgrEmail', { req: true, type: 'email', auto: 'email', ph: '승인 안내·비밀번호 찾기' })}
      </div>

      <div style={sec}>관리자 계정 <span style={{ fontWeight: 500, fontSize: 13, color: '#8B95A1' }}>— 직원 계정은 승인 후 직접 추가</span></div>
      <div style={grid}>
        {row('아이디', 'userId', { req: true, ph: '영문·숫자 4~20자', auto: 'username' })}
        {row('비밀번호', 'pw', { req: true, type: 'password', ph: '8자 이상', auto: 'new-password' })}
        <label style={{ display: 'block' }}>
          {lab('비밀번호 확인', true)}
          <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" style={cin} />
        </label>
        <label style={{ display: 'block', ...span(3) }}>
          {lab('요청 사항 (선택)')}
          <input value={f.memo} onChange={set('memo')} style={cin} />
        </label>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#4E5968', margin: '22px 0 14px', cursor: 'pointer', lineHeight: 1.6, background: '#F9FAFB', borderRadius: 12, padding: '12px 14px' }}>
        <input type="checkbox" checked={f.agree} onChange={e => setF(p => ({ ...p, agree: e.target.checked }))} style={{ marginTop: 3 }} />
        <span>
          이용약관과 개인정보 수집·이용(상호·사업자번호·대표자·주소·연락처·담당자 정보·아이디 / 가입 승인·서비스 제공·연락 / 탈퇴 요청 시까지 보관)에 동의합니다 <b style={{ color: '#F04452' }}>*</b>
          <button type="button" onClick={() => setShowTerms(true)} style={{ ...linkBtn, marginLeft: 4, textDecoration: 'underline', fontSize: 12.5 }}>전문 보기</button>
        </span>
      </label>
      {showTerms && <TermsDialog onClose={() => setShowTerms(false)} />}
      {err && <div style={{ color: '#F04452', fontSize: 13, marginBottom: 8 }}>⚠ {err}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button type="button" onClick={onBack} style={{ ...linkBtn, whiteSpace: 'nowrap' }}>← 홈으로</button>
        <button type="submit" disabled={busy} style={{ ...primary(!busy), flex: 1 }}>{busy ? '신청 중…' : '가입 신청'}</button>
      </div>
    </form>
  );
};

/** 아이디 찾기 · 비밀번호 찾기 (결과는 메일로) */
const Find: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [tab, setTab] = useState<'id' | 'pw'>('pw');
  const [a, setA] = useState('');
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = tab === 'id' ? await findId(a, email) : await resetPassword(a.trim(), email.trim());
      setMsg({ ok: true, text: r.data.message });
    } catch (ex) { setMsg({ ok: false, text: errMsg(ex) }); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} style={{ ...card, maxWidth: 400 }}>
      <div style={{ display: 'flex', marginBottom: 20, background: '#F2F4F6', borderRadius: 12, padding: 4 }}>
        {([['pw', '비밀번호 찾기'], ['id', '아이디 찾기']] as const).map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setA(''); setMsg(null); }} style={{
            flex: 1, height: 42, border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15,
            fontWeight: tab === k ? 700 : 500, color: tab === k ? '#191F28' : '#8B95A1', background: tab === k ? '#fff' : 'transparent',
            boxShadow: tab === k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          }}>{l}</button>
        ))}
      </div>
      <div style={{ fontSize: 14, color: '#4E5968', marginBottom: 20, lineHeight: 1.6 }}>
        {tab === 'pw' ? '임시 비밀번호를 메일로 보내 드립니다. 로그인하면 새 비밀번호를 정합니다.' : '가입할 때 쓴 사업자등록번호와 담당자 이메일을 넣으면 아이디를 메일로 보내 드립니다.'}
        <br />직원 계정은 회사 관리자에게 비밀번호 변경을 요청할 수도 있습니다.
      </div>
      <label style={labelSt}>{tab === 'pw' ? '아이디' : '사업자등록번호'}</label>
      <input value={a} onChange={e => setA(tab === 'id' ? fmtBiz(e.target.value) : e.target.value)} autoFocus autoComplete={tab === 'pw' ? 'username' : 'off'}
        style={{ ...input, marginBottom: 16 }} />
      <label style={labelSt}>이메일 {tab === 'pw' && '(본인 이메일 또는 회사 담당자 이메일)'}</label>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" style={{ ...input, marginBottom: 20 }} />
      {msg && <div style={{ fontSize: 13, marginBottom: 12, color: msg.ok ? '#03B26C' : '#F04452', lineHeight: 1.5 }}>{msg.ok ? '✉ ' : '⚠ '}{msg.text}</div>}
      <button type="submit" disabled={busy || !a || !email} style={primary(!(busy || !a || !email))}>{busy ? '보내는 중…' : '메일로 받기'}</button>
      <div style={{ textAlign: 'center', marginTop: 12 }}><button type="button" onClick={onBack} style={linkBtn}>← 로그인으로</button></div>
    </form>
  );
};

/** 첫 로그인(관리자가 만든 계정·임시 비밀번호) → 비밀번호부터 바꿈 */
const ForceChange: React.FC<{ user: AuthUser; onDone: () => void; onLogout: () => void }> = ({ user, onDone, onLogout }) => {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const bad = next.length < 8 ? '새 비밀번호는 8자 이상' : next !== next2 ? '새 비밀번호가 서로 다릅니다' : next === cur ? '받은 비밀번호와 다른 비밀번호로 정하세요' : null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bad) { setErr(bad); return; }
    try { await changePassword(cur, next); onDone(); } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <form onSubmit={submit} style={{ ...card, maxWidth: 400 }}>
      <h2 style={{ margin: '0 0 8px', color: '#191F28', fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }}>비밀번호를 새로 정해 주세요</h2>
      <div style={{ fontSize: 14, color: '#4E5968', marginBottom: 22, lineHeight: 1.6 }}>{user.compName} · {user.name}({user.id}) — 관리자가 정해 준 비밀번호는 처음 한 번만 쓸 수 있습니다.</div>
      <label style={labelSt}>받은 비밀번호</label>
      <input type="password" value={cur} onChange={e => setCur(e.target.value)} autoComplete="current-password" autoFocus style={{ ...input, marginBottom: 16 }} />
      <label style={labelSt}>새 비밀번호 (8자 이상)</label>
      <input type="password" value={next} onChange={e => setNext(e.target.value)} autoComplete="new-password" style={{ ...input, marginBottom: 16 }} />
      <label style={labelSt}>새 비밀번호 확인</label>
      <input type="password" value={next2} onChange={e => setNext2(e.target.value)} autoComplete="new-password" style={{ ...input, marginBottom: 20 }} />
      {err && <div style={{ color: '#F04452', fontSize: 13, marginBottom: 10 }}>⚠ {err}</div>}
      <button type="submit" disabled={!cur || !next} style={primary(!!cur && !!next)}>비밀번호 바꾸고 시작</button>
      <div style={{ textAlign: 'center', marginTop: 12 }}><button type="button" onClick={onLogout} style={linkBtn}>로그아웃</button></div>
    </form>
  );
};

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(!!getToken());
  // home = 홈페이지(처음), demo = 로그인 없이 도면 체험 (QR로 들어오면 바로 로그인)
  const [mode, setMode] = useState<'home' | 'demo' | 'login' | 'signup' | 'done' | 'find'>(() => (new URLSearchParams(window.location.search).get('p') ? 'login' : 'home'));
  const [id, setId] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 저장된 토큰이 아직 유효한지 확인
  useEffect(() => {
    if (!getToken()) return;
    fetchMe().then(r => setUser(r.data)).catch(() => setToken(null)).finally(() => setChecking(false));
  }, []);
  useEffect(() => {
    const out = () => setUser(null);
    window.addEventListener('pd-logout', out);
    return () => window.removeEventListener('pd-logout', out);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await login(id.trim(), pw);
      setToken(r.data.token);
      setUser(r.data.user);
      setPw('');
    } catch (ex) {
      setErr(errMsg(ex));
    } finally { setBusy(false); }
  };

  if (checking) return <div style={{ padding: 40, color: '#888' }}>확인 중…</div>;
  // 앱 전체가 스크롤 고정이라(도면 화면) 이 화면들은 자기 안에서 스크롤 — 긴 가입 양식도 끝까지 내려감
  const page: React.CSSProperties = { height: '100vh', overflowY: 'auto', display: 'flex', alignItems: 'safe center' as any, justifyContent: 'center', background: '#F2F4F6', padding: 16, boxSizing: 'border-box' };
  if (user?.mustChange) {
    return <div style={page}><ForceChange user={user} onDone={() => setUser({ ...user, mustChange: false })} onLogout={() => { setToken(null); setUser(null); }} /></div>;
  }
  if (user) {
    return <AuthCtx.Provider value={{ user, logout: () => { setToken(null); setUser(null); setMode('home'); }, update: setUser }}>{children}</AuthCtx.Provider>;
  }
  if (mode === 'demo') {
    return <AuthCtx.Provider value={{ user: GUEST, logout: () => setMode('home'), toSignup: () => setMode('signup'), toLogin: () => setMode('login') }}>{children}</AuthCtx.Provider>;
  }
  if (mode === 'home') return <Landing onDemo={() => setMode('demo')} onLogin={() => setMode('login')} onSignup={() => setMode('signup')} />;
  if (mode === 'find') return <div style={page}><Find onBack={() => setMode('login')} /></div>;
  if (mode === 'signup') return <div style={page}><Signup onDone={() => setMode('done')} onBack={() => setMode('home')} /></div>;
  if (mode === 'done') {
    return (
      <div style={page}>
        <div style={{ ...card, maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>✅</div>
          <h2 style={{ margin: '12px 0 10px', color: '#191F28', fontSize: 22, fontWeight: 800 }}>가입 신청이 접수되었습니다</h2>
          <div style={{ fontSize: 15, color: '#4E5968', lineHeight: 1.7 }}>관리자 승인이 끝나면 신청한 아이디로 로그인할 수 있습니다.<br />승인 전에 로그인하면 "승인 대기 중"으로 안내됩니다.</div>
          <button onClick={() => setMode('login')} style={{ ...primary(true), marginTop: 24 }}>로그인 화면으로</button>
        </div>
      </div>
    );
  }
  return (
    <div style={page}>
      <form onSubmit={submit} style={{ ...card, maxWidth: 400 }}>
        <Brand />
        <h1 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: '#191F28', letterSpacing: -0.5 }}>로그인</h1>
        <div style={{ fontSize: 14, color: '#8B95A1', marginBottom: 26 }}>냉동창고 패널 도면 · 생산 · 출고</div>
        <label style={labelSt}>아이디</label>
        <input value={id} onChange={e => setId(e.target.value)} autoFocus autoComplete="username" style={{ ...input, marginBottom: 16 }} />
        <label style={labelSt}>비밀번호</label>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="current-password" style={{ ...input, marginBottom: 20 }} />
        {err && <div style={{ color: '#F04452', fontSize: 14, marginBottom: 14 }}>⚠ {err}</div>}
        <button type="submit" disabled={busy || !id || !pw} style={primary(!(busy || !id || !pw))}>{busy ? '로그인 중…' : '로그인'}</button>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button type="button" onClick={() => { setErr(null); setMode('find'); }} style={{ ...linkBtn, color: '#8B95A1', fontWeight: 500 }}>아이디 · 비밀번호 찾기</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 14, color: '#8B95A1' }}>
          처음이신가요? <button type="button" onClick={() => { setErr(null); setMode('signup'); }} style={{ ...linkBtn, fontWeight: 'bold' }}>회사 가입 신청</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 8 }}><button type="button" onClick={() => setMode('home')} style={{ ...linkBtn, color: '#8B95A1', fontWeight: 500 }}>← 홈으로</button></div>
      </form>
    </div>
  );
};

export default AuthGate;
